import { compareDesc } from "date-fns";
import { deburr } from "lodash-es";

import { ComputedRole, getCanViewPage } from "../../shared/capabilities";

import {
  ActivityCategory,
  ActivityPageType,
  ActivityType,
  PageType,
  SectionType,
  UserInformationType,
  UserType,
} from "components/server-types";
import { DEFAULT_ORDER_FN, ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import type { ActivityWithPagesType, UserWithSectionActivity } from "store/selectors/types";
import { SectionUserActivityStatus } from "store/selectors/types";

const ORDERED_ACTIVITY_CATEGORIES = Object.values(ActivityCategory);
export const sortActivities = (a: ActivityType, b: ActivityType) => {
  const prioA = ORDERED_ACTIVITY_CATEGORIES.indexOf(a.category);
  const prioB = ORDERED_ACTIVITY_CATEGORIES.indexOf(b.category);

  if (prioA === prioB) {
    return a.order - b.order;
  }

  return prioA - prioB;
};

export const getSortedPages = (activities: ActivityWithPagesType[]) => {
  const pages: ActivityPageType[] = [];
  for (const activity of activities) {
    pages.push(...activity.pages.sort(DEFAULT_ORDER_FN));
  }
  return pages;
};

export const sortPagesForTopic = (
  topicId: string,
  activities: ActivityType[],
  pages: PageType[],
) => {
  const sortedActivities = activities
    .filter((activity) => activity.topic_id === topicId)
    .sort(sortActivities);

  return getSortedPages(
    sortedActivities.map((activity) => ({
      ...activity,
      pages: pages.filter((page) => page.activity_id === activity.id),
    })) as ActivityWithPagesType[],
  );
};

export const sortTopicActivities = (
  topicId: string,
  activities: ActivityType[],
  pages: PageType[],
  computedRole: ComputedRole,
) => {
  const sortedActivities = activities
    .filter((activity) => activity.topic_id === topicId)
    .sort(sortActivities);

  return sortedActivities.map((activity) => {
    const activityPages = pages.filter(
      (page) => page.activity_id === activity.id && getCanViewPage(page, computedRole),
    );
    return { ...activity, pages: activityPages } as ActivityWithPagesType;
  });
};

export const sortByTermYearRegex = <T extends { title: string }>(a: T, b: T) => {
  // Extract valid years: 4-digit years between 2000-2050, or 2-digit years between 00-50
  const getValidYear = (title: string): { year: number; is2Digit: boolean } | null => {
    // Try 4-digit year first
    const fourDigitMatch = title.match(/(\d{4})/);
    if (fourDigitMatch) {
      const year = parseInt(fourDigitMatch[1], 10);
      if (year >= 2000 && year <= 2050) {
        return { year, is2Digit: false };
      }
    }

    // Try 2-digit year (treat as 20XX)
    const twoDigitMatch = title.match(/\b(\d{2})\b/);
    if (twoDigitMatch) {
      const year = parseInt(twoDigitMatch[1], 10);
      if (year >= 0 && year <= 50) {
        return { year: 2000 + year, is2Digit: true };
      }
    }

    return null;
  };

  const aYearInfo = getValidYear(a.title);
  const bYearInfo = getValidYear(b.title);

  // If no year, sort by title alphabetically
  if (!aYearInfo && !bYearInfo) {
    // eslint-disable-next-line babel/new-cap
    return ORDER_BY_TITLE(a, b);
  }

  // Overall, terms with years should appear before terms without
  if (aYearInfo && !bYearInfo) {
    return -1;
  }
  if (!aYearInfo && bYearInfo) {
    return 1;
  }

  // Both have years - sort by year descending first
  if (aYearInfo!.year !== bYearInfo!.year) {
    return bYearInfo!.year - aYearInfo!.year; // Descending order
  }

  // Same year - custom sorting that prioritizes shorter year formats
  // Extract the non-year part of the title for comparison
  const getNonYearPart = (title: string, yearInfo: { year: number; is2Digit: boolean }) => {
    if (yearInfo.is2Digit) {
      // Remove 2-digit year
      return title.replace(/\b\d{2}\b/, "").trim();
    }
    // Remove 4-digit year
    return title.replace(/\d{4}/, "").trim();
  };

  const aNonYear = getNonYearPart(a.title, aYearInfo!);
  const bNonYear = getNonYearPart(b.title, bYearInfo!);

  // If the non-year parts are the same, prioritize 2-digit format
  if (aNonYear === bNonYear) {
    if (aYearInfo!.is2Digit !== bYearInfo!.is2Digit) {
      return aYearInfo!.is2Digit ? -1 : 1; // 2-digit comes first
    }
  }

  // Otherwise sort alphabetically
  // eslint-disable-next-line babel/new-cap
  return ORDER_BY_TITLE(a, b);
};

const transformForSort = (name: string) => deburr(name).toLowerCase();

export const sortByFirstName = (a: UserInformationType, b: UserInformationType) => {
  const aKey =
    `${transformForSort(a.first_name ?? "")} ${transformForSort(a.last_name ?? "")}`.trim();
  const bKey =
    `${transformForSort(b.first_name ?? "")} ${transformForSort(b.last_name ?? "")}`.trim();
  return aKey.localeCompare(bKey, undefined, { sensitivity: "base", numeric: true });
};

export const getActiveStateSortKeyForUser = (
  user: Omit<UserWithSectionActivity, "section_user_id">,
) => {
  return ["active", "inactive", "offline"].indexOf(user.sectionActivityStatus) > 1 ? 1 : 0;
};

export const sortByActiveStateThenFirstName = (
  a: Omit<UserWithSectionActivity, "section_user_id">,
  b: Omit<UserWithSectionActivity, "section_user_id">,
) => {
  const activeStateDiff = getActiveStateSortKeyForUser(a) - getActiveStateSortKeyForUser(b);
  if (activeStateDiff !== 0) return activeStateDiff;
  return sortByFirstName(a, b);
};

const getActiveStateSortKey = (status: SectionUserActivityStatus | undefined) =>
  status === SectionUserActivityStatus.OFFLINE || status == null ? 1 : 0;

/** Lookups for people-list order (from memoized selectors). */
export type PeopleListSortLookups = {
  classUserIdByUserId: Readonly<Record<string, string>>;
  handRaisedAtByUserId: Readonly<Record<string, string>>;
  isFollowingByUserId: Readonly<Record<string, true>>;
  activityStatusByUserId: Readonly<Record<string, SectionUserActivityStatus>>;
  usersById: Record<string, UserType | undefined>;
  presentingUserId?: string | null;
  screensharingClassUserId?: string | null;
};

/** Sidebar people list order: screenshare → presenting → hand raise → following → online → name. */
export const compareUserIdsForPeopleList = (
  userIdA: string,
  userIdB: string,
  {
    classUserIdByUserId,
    handRaisedAtByUserId,
    isFollowingByUserId,
    activityStatusByUserId,
    usersById,
    presentingUserId,
    screensharingClassUserId,
  }: PeopleListSortLookups,
) => {
  if (screensharingClassUserId) {
    if (classUserIdByUserId[userIdA] === screensharingClassUserId) return -1;
    if (classUserIdByUserId[userIdB] === screensharingClassUserId) return 1;
  }

  if (presentingUserId) {
    if (userIdA === presentingUserId) return -1;
    if (userIdB === presentingUserId) return 1;
  }

  const handRaisedAtA = handRaisedAtByUserId[userIdA];
  const handRaisedAtB = handRaisedAtByUserId[userIdB];
  if (handRaisedAtA && handRaisedAtB) {
    return compareDesc(handRaisedAtB, handRaisedAtA);
  }
  if (handRaisedAtA) return -1;
  if (handRaisedAtB) return 1;

  const isFollowingA = !!isFollowingByUserId[userIdA];
  const isFollowingB = !!isFollowingByUserId[userIdB];
  if (isFollowingA && !isFollowingB) return -1;
  if (!isFollowingA && isFollowingB) return 1;

  const activeStateDiff =
    getActiveStateSortKey(activityStatusByUserId[userIdA]) -
    getActiveStateSortKey(activityStatusByUserId[userIdB]);
  if (activeStateDiff !== 0) return activeStateDiff;

  const userA = usersById[userIdA];
  const userB = usersById[userIdB];
  if (userA && userB) {
    return sortByFirstName(userA, userB);
  }
  if (userA) return -1;
  if (userB) return 1;
  return 0;
};

/** Archived-last, then title. */
export const compareSectionsByActiveTitle = (
  left: Pick<SectionType, "id" | "title">,
  right: Pick<SectionType, "id" | "title">,
  archivedSectionIds: ReadonlySet<string>,
) => {
  const leftArchived = archivedSectionIds.has(left.id);
  const rightArchived = archivedSectionIds.has(right.id);
  if (leftArchived !== rightArchived) {
    return leftArchived ? 1 : -1;
  }
  // eslint-disable-next-line babel/new-cap
  return ORDER_BY_TITLE(left, right);
};
